import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Car, LogIn, Shield, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Logo URL
const LOGO_URL = "https://customer-assets.emergentagent.com/job_ppf-auto-manager/artifacts/h7byux7s_UP-Logo-White.png";
// Background Image URL
const BG_IMAGE_URL = "https://customer-assets.emergentagent.com/job_ppfwarranty/artifacts/vlnwkut7_UPAPPBACK.jpg";

export default function HomePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#09090b] relative overflow-hidden">
      {/* Background Image with 15% Opacity */}
      <div 
        className="absolute inset-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${BG_IMAGE_URL}')`,
          opacity: 0.15
        }}
      />
      {/* Dark overlay for better text visibility */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#09090b]/30 via-[#09090b]/40 to-[#09090b]/70" />

      {/* Red Glow Effect */}
      <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-red-600/10 rounded-full blur-[150px] pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header - Logo with extra top spacing to position slightly below center */}
        <header className="pt-24 md:pt-32 pb-6 px-6 lg:px-12">
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-center"
          >
            <img src={LOGO_URL} alt="Unique Protection" className="h-16 md:h-20 w-auto" />
          </motion.div>
        </header>

        {/* Main Content */}
        <main className="flex-1 flex flex-col items-center justify-center px-6 py-12">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-center max-w-3xl"
          >
            <h1 className="font-['Chivo'] text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 tracking-tight">
              Premium Paint
              <span className="text-red-500"> Protection</span>
              <br />Film Solutions
            </h1>
            <p className="text-base lg:text-lg text-zinc-400 mb-12 max-w-xl mx-auto">
              Protect your vehicle with industry-leading PPF technology.
              <br />
              Track your warranty. Ensure authenticity.
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Button
                  data-testid="check-my-car-btn"
                  onClick={() => navigate('/check-vin')}
                  className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white font-bold py-6 px-8 rounded-lg shadow-[0_0_30px_rgba(220,38,38,0.4)] hover:shadow-[0_0_40px_rgba(220,38,38,0.6)] text-lg gap-3"
                >
                  <Car className="w-5 h-5" />
                  Check My Car
                </Button>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.5 }}
              >
                <Button
                  data-testid="login-btn"
                  onClick={() => navigate('/login')}
                  variant="outline"
                  className="w-full sm:w-auto bg-white/5 border-white/10 hover:bg-white/10 hover:border-white/20 text-white font-bold py-6 px-8 rounded-lg backdrop-blur-sm text-lg gap-3"
                >
                  <LogIn className="w-5 h-5" />
                  Partner Login
                </Button>
              </motion.div>
            </div>
          </motion.div>

          {/* Features */}
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mt-20 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl w-full"
          >
            {[
              { icon: Shield, title: 'Verified Protection', desc: 'Track authentic PPF installations' },
              { icon: Car, title: 'Warranty Lookup', desc: 'Check any vehicle by VIN & code' },
              { icon: CheckCircle, title: 'Full Coverage', desc: 'View complete installation records' },
            ].map((feature, i) => (
              <div 
                key={feature.title}
                className="bg-zinc-900/60 backdrop-blur-xl border border-white/10 rounded-lg p-6 text-center hover:border-red-500/30 transition-colors"
              >
                <feature.icon className="w-8 h-8 text-red-500 mx-auto mb-4" />
                <h3 className="font-['Chivo'] text-lg font-bold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-zinc-400">{feature.desc}</p>
              </div>
            ))}
          </motion.div>
        </main>

        {/* Footer */}
        <footer className="py-6 px-6 text-center">
          <p className="text-sm text-zinc-600">© 2026 Unique Protection. All rights reserved.</p>
        </footer>
      </div>
    </div>
  );
}
